import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiService } from '../../../shared/services';
import { Observable } from 'rxjs';
import { urlList } from '../../../shared/services/service-list';
import { shareReplay } from 'rxjs/operators';
import { deepmerge } from '../../../shared/utils';
import { TransactionsUpdateTicketPayload } from '../../models/transactions.model';
import { STORAGE_KEYS } from '../../../shared/models';
import { FormArray } from '@angular/forms';
import { StateManager } from '@shared/services/state-manager/state-manager.service';
import { buildCreateTicketPayload } from './transaction-ticket-payload';

@Injectable({
  providedIn: 'root',
})
export class TransactionsService {
  private _contextData: any;
  private _subject: any;
  private _ticket: any;
  private _cif: any;
  private _currentTicketId: string | null = null; // Track active ticket ID
  stateManager = inject(StateManager);

  constructor(
    private api: ApiService,
    private http: HttpClient
  ) {
    if (!this.contextData) this.contextData = {};

    // Cleanup old tickets on service initialization (keep last 10)
    this.cleanupOldTickets(10);
  }

  /**
   * Generate ticket-scoped storage key
   * @param baseKey - Base storage key
   * @param ticketId - Ticket ID to scope the key
   */
  private getTicketScopedKey(baseKey: string, ticketId: string): string {
    return `${baseKey}_${ticketId}`;
  }

  /**
   * Get current active ticket ID from memory or localStorage
   */
  private getCurrentTicketId(): string | null {
    if (this._currentTicketId) {
      return this._currentTicketId;
    }
    // Fallback: try to get from stored ticket
    const storedTicket = this._ticket || this.retrieve(STORAGE_KEYS.TICKET_RESPONSE);
    return storedTicket?.id || null;
  }

  set ticket(data: any) {
    if (!data) {
      // Clear ticket-scoped storage if we have a current ticket ID
      const ticketId = this.getCurrentTicketId();
      if (ticketId) {
        this.remove(this.getTicketScopedKey(STORAGE_KEYS.TICKET_RESPONSE, ticketId));
        this.remove(this.getTicketScopedKey(STORAGE_KEYS.CONTEXT_DATA, ticketId));
      }
      // Also clear non-scoped for backward compatibility
      this.remove(STORAGE_KEYS.TICKET_RESPONSE);
      this.remove(STORAGE_KEYS.CONTEXT_DATA);
      this._currentTicketId = null;
      this._ticket = null;
      return;
    }

    this._ticket = data;
    const ticketId = data.id;

    if (ticketId) {
      // Store with ticket-scoped key
      this._currentTicketId = ticketId;
      this.stateManager.update(this.getTicketScopedKey(STORAGE_KEYS.TICKET_RESPONSE, ticketId), this._ticket, false);

      // Save any in-memory contextData to ticket-scoped storage
      // This handles the case where contextData was set BEFORE ticket creation (e.g., from money-transfer dialog)
      if (this._contextData) {
        // Safety check: Verify ticket ID consistency before persisting
        const actualTicketId = this._ticket?.id;

        if (actualTicketId && actualTicketId.toString() === ticketId.toString()) {
          const storageKey = this.getTicketScopedKey(STORAGE_KEYS.CONTEXT_DATA, actualTicketId);

          // Additional safety: Verify the key contains the actual ticket ID
          if (storageKey.includes(actualTicketId.toString())) {
            this.stateManager.update(storageKey, this._contextData, false);
          }
        }
      }
    } else {
      // Fallback to non-scoped storage if no ticket ID
      this.stateManager.update(STORAGE_KEYS.TICKET_RESPONSE, this._ticket, false);
    }
  }

  get ticket(): any {
    // Try to get from memory first
    if (this._ticket) {
      return this._ticket;
    }

    // Try ticket-scoped storage if we have a current ticket ID
    const ticketId = this._currentTicketId;
    if (ticketId) {
      const scopedTicket = this.retrieve(this.getTicketScopedKey(STORAGE_KEYS.TICKET_RESPONSE, ticketId));
      if (scopedTicket) {
        this._ticket = scopedTicket;
        return scopedTicket;
      }
    }

    // Fallback to non-scoped storage (backward compatibility)
    const ticket = this.retrieve(STORAGE_KEYS.TICKET_RESPONSE);
    if (ticket) {
      this._ticket = ticket;
      this._currentTicketId = ticket.id || null;
    }
    return ticket;
  }

  public patchTicket(updates: object): void {
    this._ticket = deepmerge(this.ticket || {}, updates);
    const ticketId = this._ticket?.id;

    if (ticketId) {
      this._currentTicketId = ticketId;
      this.stateManager.update(this.getTicketScopedKey(STORAGE_KEYS.TICKET_RESPONSE, ticketId), this._ticket, false);
    } else {
      // Fallback to non-scoped storage
      this.stateManager.update(STORAGE_KEYS.TICKET_RESPONSE, this._ticket, false);
    }
  }

  set cif(data: any) {
    if (!data) {
      this.remove(STORAGE_KEYS.CIF_RESPONSE);
      return;
    }
    this._cif = data;
    this.stateManager.update(STORAGE_KEYS.CIF_RESPONSE, this._cif, false);
  }

  get cif(): any {
    return this.retrieve(STORAGE_KEYS.CIF_RESPONSE);
  }

  patchCif(updates: any): void {
    this._cif = deepmerge(this.cif || {}, updates);
    this.stateManager.update(STORAGE_KEYS.CIF_RESPONSE, this._cif, false);
  }

  set subject(data: any) {
    if (!data) {
      this.remove(STORAGE_KEYS.SUBJECT);
      return;
    }
    this._subject = data;
    this.stateManager.update(STORAGE_KEYS.SUBJECT, this._subject, false);
  }

  get subject(): any {
    return this.retrieve(STORAGE_KEYS.SUBJECT);
  }

  patchSubject(updates: any): void {
    this._subject = deepmerge(this.subject || {}, updates);
    this.stateManager.update(STORAGE_KEYS.SUBJECT, this._subject, false);
  }

  set contextData(obj) {
    this._contextData = deepmerge(this._contextData, obj);
    const ticketId = this.getCurrentTicketId();

    if (ticketId) {
      // Safety check: Verify ticket ID consistency before persisting
      const currentTicket = this._ticket || this.retrieve(STORAGE_KEYS.TICKET_RESPONSE);
      const actualTicketId = currentTicket?.id;

      if (actualTicketId && actualTicketId.toString() === ticketId.toString()) {
        const storageKey = this.getTicketScopedKey(STORAGE_KEYS.CONTEXT_DATA, actualTicketId);

        // Additional safety: Verify the key contains the actual ticket ID
        if (storageKey.includes(actualTicketId.toString())) {
          // Use actualTicketId (verified ground truth) for the localStorage key
          this.stateManager.update(storageKey, this._contextData, false);
          // Update _currentTicketId to stay in sync with actual ticket
          this._currentTicketId = actualTicketId.toString();
        }
      }
    }
    // If no ticketId, keep contextData in memory only until ticket is created
  }

  get contextData(): { [key: string]: any } {
    // Try to get from memory first
    if (this._contextData) {
      return this._contextData;
    }

    // Try ticket-scoped storage if we have a current ticket ID
    const ticketId = this.getCurrentTicketId();
    if (ticketId) {
      // Safety check: Verify ticket ID consistency before loading
      const currentTicket = this._ticket || this.retrieve(STORAGE_KEYS.TICKET_RESPONSE);
      const actualTicketId = currentTicket?.id;

      if (actualTicketId && actualTicketId.toString() === ticketId.toString()) {
        const storageKey = this.getTicketScopedKey(STORAGE_KEYS.CONTEXT_DATA, actualTicketId);

        // Additional safety: Verify the key contains the actual ticket ID
        if (storageKey.includes(actualTicketId.toString())) {
          // Use actualTicketId (verified ground truth) for the localStorage key
          const scopedContext = this.retrieve(storageKey);
          if (scopedContext) {
            this._contextData = scopedContext;
            // Update _currentTicketId to stay in sync with actual ticket
            this._currentTicketId = actualTicketId.toString();
            return scopedContext;
          }
        }
      }
    }

    // No ticket ID and no memory cache - return empty object
    // contextData will be populated from dialog or form data
    return {};
  }

  patchContextData(kv: {}): void {
    let config = this.contextData;
    // @ts-ignore
    Object.keys(kv)
      // @ts-ignore
      .filter((i: string) => !kv[`${i}`])
      .forEach(k => {
        delete config[`${k}`];
      });
    // @ts-ignore
    Object.keys(kv)
      // @ts-ignore
      .filter((i: string) => kv[`${i}`])
      .forEach(j => {
        let o = {};
        // @ts-ignore
        o[`${j}`] = kv[`${j}`];
        // @ts-ignore
        config[`${j}`] = kv[`${j}`];
        config = deepmerge(config, o);
      });

    this._contextData = config;
    const ticketId = this.getCurrentTicketId();

    if (ticketId) {
      // Safety check: Verify ticket ID consistency before persisting
      const currentTicket = this._ticket || this.retrieve(STORAGE_KEYS.TICKET_RESPONSE);
      const actualTicketId = currentTicket?.id;

      if (actualTicketId && actualTicketId.toString() === ticketId.toString()) {
        const storageKey = this.getTicketScopedKey(STORAGE_KEYS.CONTEXT_DATA, actualTicketId);

        // Additional safety: Verify the key contains the actual ticket ID
        if (storageKey.includes(actualTicketId.toString())) {
          // Use actualTicketId (verified ground truth) for the localStorage key
          this.stateManager.update(storageKey, config, false);
          // Update _currentTicketId to stay in sync with actual ticket
          this._currentTicketId = actualTicketId.toString();
        }
      }
    }
    // If no ticketId, keep contextData in memory only until ticket is created
  }

  /**
   * Clear contextData from localStorage for a specific ticket
   * Called when component is destroyed to clean up ticket-scoped storage
   */
  clearContextData(ticketId?: string): void {
    const idToUse = ticketId || this.getCurrentTicketId();

    if (!idToUse) {
      return;
    }

    const storageKey = this.getTicketScopedKey(STORAGE_KEYS.CONTEXT_DATA, idToUse);
    this.stateManager.delete(storageKey);

    // If clearing the current ticket's context, also clear memory cache
    if (idToUse.toString() === this._currentTicketId?.toString()) {
      this._contextData = {};
    }
  }

  reset(): void {
    // Clear ticket-scoped storage if we have a current ticket ID
    const ticketId = this.getCurrentTicketId();
    if (ticketId) {
      this.clearTicketData(ticketId);
    }

    // Clear non-scoped storage for backward compatibility
    this.stateManager.delete(STORAGE_KEYS.CIF_RESPONSE);
    this.stateManager.delete(STORAGE_KEYS.CONTEXT_DATA);
    this.stateManager.delete(STORAGE_KEYS.TICKET_RESPONSE);

    // Reset in-memory state
    this._currentTicketId = null;
    this._ticket = null;
    this._contextData = null;
  }

  /**
   * Switch to a different ticket by ID
   * Loads ticket and context data from localStorage
   */
  public setActiveTicket(ticketId: string): void {
    if (!ticketId) {
      return;
    }

    this._currentTicketId = ticketId;

    // Load ticket data from storage
    const scopedTicket = this.retrieve(this.getTicketScopedKey(STORAGE_KEYS.TICKET_RESPONSE, ticketId));
    if (scopedTicket) {
      this._ticket = scopedTicket;
    }

    // Load context data from storage
    // Only update cache if scoped context exists for this ticket
    const scopedContext = this.retrieve(this.getTicketScopedKey(STORAGE_KEYS.CONTEXT_DATA, ticketId));
    if (scopedContext) {
      this._contextData = scopedContext;
    }
    // If no scoped context, keep existing _contextData (for new transactions)
    // The contextData getter will handle fallbacks appropriately
  }

  /**
   * Clear data for a specific ticket
   */
  public clearTicketData(ticketId: string): void {
    if (!ticketId) {
      return;
    }

    this.stateManager.delete(this.getTicketScopedKey(STORAGE_KEYS.TICKET_RESPONSE, ticketId));
    this.stateManager.delete(this.getTicketScopedKey(STORAGE_KEYS.CONTEXT_DATA, ticketId));

    if (this._currentTicketId === ticketId) {
      this._currentTicketId = null;
      this._ticket = null;
      this._contextData = null;
    }
  }

  /**
   * Cleanup old ticket data from localStorage
   * Keeps only the most recent 'maxTickets' tickets
   */
  public cleanupOldTickets(maxTickets: number = 10): void {
    const ticketKeys: { key: string; ticketId: string; timestamp: number }[] = [];

    // Find all ticket-scoped keys in localStorage
    for (const key in this.stateManager) {
      if (key.startsWith(`${STORAGE_KEYS.TICKET_RESPONSE}_`)) {
        const ticketId = key.replace(`${STORAGE_KEYS.TICKET_RESPONSE}_`, '');
        const ticketData = this.stateManager[key];
        const timestamp = ticketData?.createdAt || ticketData?.lastModified || 0;
        ticketKeys.push({ key, ticketId, timestamp });
      }
    }

    // Sort by timestamp (newest first)
    ticketKeys.sort((a, b) => b.timestamp - a.timestamp);

    // Remove tickets beyond maxTickets
    if (ticketKeys.length > maxTickets) {
      const ticketsToRemove = ticketKeys.slice(maxTickets);
      ticketsToRemove.forEach(({ ticketId }) => {
        this.clearTicketData(ticketId);
      });
    }
  }

  private remove(k: string): void {
    this.stateManager.delete(k);
  }

  private retrieve(k: string, _default?: any): any {
    return this.stateManager[k] ?? _default ?? null;
  }

  public fetchTransferTypeList(): Observable<any> {
    const url = urlList.getTransactions.getTransferType;
    return this.http.get<any>(url).pipe(shareReplay(1));
  }

  public createTransactionTicket(data: Record<string, unknown>): Observable<any> {
    const url = `/v1/transactions/create-ticket`;
    return this.api.post<any>(url, buildCreateTicketPayload(data));
  }

  public fetchTicketById(ticketId: string): Observable<any> {
    return this.api.get('/v1/tickets/' + ticketId);
  }

  public submitTicket(id: any, data: any, type: any) {
    return this.api.post<any>(`/v1/transactions/${id}/submit-${type}`, data);
  }

  public uploadDocuments(value: any) {
    return this.api.post<any>(`/v1/documents/submit`, value);
  }

  public updateTicketData(ticketId: any, data: any) {
    return this.api.post(`/v1/tickets/${ticketId}/data`, data);
  }

  public getIsRequestingBranchHeadoffice() {
    return this.api.get<any>(`/v1/backoffice/user/isheadoffice`);
  }

  public getDenominationsData(countryCode: string, transactionType: string) {
    return this.api.get(`/v1/transactions/denominations?countryCode=${countryCode}&transactionType=${transactionType}`);
  }

  public searchBICs(query: string, countryCode?: string, txnType?: string): Observable<any> {
    let transactionsUrl: string;
    if (txnType === 'SWIFT' || txnType === 'EAPS' || txnType === 'REPS') {
      transactionsUrl = `/v1/transactions/bics?search=${query}&onlyhqs=true&skip=0&fetch=200`;
    } else {
      transactionsUrl = `/v1/transactions/bics?search=${query}&country=${countryCode}&onlyhqs=false&skip=0&fetch=200`;
    }
    return this.api.get<any>(transactionsUrl, {
      headers: { skipLoadingInterceptor: 'true' },
    });
  }

  public getCreditName(creditAccountNumber: string, bankId: string = '54'): Observable<any> {
    return this.api.get<any>(`/v1/account/glenquiry/${creditAccountNumber}?bankId=${bankId}`);
  }

  public getExchangeRates(data: {
    accountNumber: string;
    fromCurrency: string;
    toCurrency: string;
    fromAmount: number;
  }): Observable<any> {
    // return this.api.post<any>(`/v1/account/exchangeRate`, data, {headers: {skipLoadingInterceptor: "true"}});
    return this.api.post<any>(`/v1/account/exchangeRate`, data);
  }

  public getSpecialExchangeRates(data: { BankID: any; Cif: any; RefNo: any }): Observable<any> {
    return this.api.post<any>(`/v1/account/specialrate`, data);
  }

  public getCharges(
    beneficiaryCountryCode: string,
    productCode: string,
    remittanceAmount: string,
    chargeOption: string,
    remittanceCurrency: string,
    accountNumber: string
  ): Observable<any> {
    const channel = 'branch';
    const url = `/v1/transactions/charges/mt-103?beneficiaryCountryCode=${beneficiaryCountryCode}&productCode=${productCode}&remittanceAmount=${remittanceAmount}&chargeOption=${chargeOption}&remittanceCurrency=${remittanceCurrency}&accountNumber=${accountNumber}&channel=${channel}`;
    return this.api.get<any>(url);
  }

  public validateInstrumentNumber(data: {
    bankId: any;
    instrumentDate: string;
    instrumentType: any;
    instrumentnum: any;
    accountNumber: any;
  }): Observable<any> {
    return this.api.post<any>(`/v1/account/chequestatus`, data);
  }

  public submitTicketToNewGen(data: TransactionsUpdateTicketPayload): Observable<any> {
    return this.api.post(`/v1/backoffice/update`, data);
  }

  public getTicketDocs(data: any): Observable<any> {
    const url = '/v1/documents/search';
    return this.api.post<any>(url, data);
  }

  public getTicketDocsV3(data: any): Observable<any> {
    const url = '/v3/documents/search';
    return this.api.post<any>(url, data);
  }

  public getTicketDocsV2(data: any): Observable<any> {
    const url = '/v2/documents/download';
    // @ts-ignore
    return this.api.postBlob<any>(url, data);
  }
  public getTicketDoc(data: any): Observable<any> {
    const url = '/v1/documents/download';
    // @ts-ignore
    return this.api.postBlob<any>(url, data);
  }

  // PURPOSE CODE NEW ENDPOINT
  // OLD: https://api-uat.equitygroupholdings.com/v1/datalookup/purposecodes/${countryCode}/SWIFTISO20022
  // UPDATED TO: https://api-uat.equitygroupholdings.com/v1/datalookup/purposecodes/${countryCode}/SWIFTISO20022/UAE ==> Its either UAE or GLOBAL
  // WIKI: https://dev.azure.com/ICT-DevOps/Omni-Channel/_wiki/wikis/Omni-Channel%20Specification/10158/Payment-Purpose-Codes

  public getPurposeCode(countryCode: string, region: string = 'GLOBAL') {
    const url = `/v1/datalookup/purposecodes/${countryCode}/SWIFTISO20022/${region}`;
    return this.api.get<any>(url);
  }

  public getDropdownData(type: string): Observable<any> {
    const url = `/v1/transactions/dropdowns?transactionType=${type}`;
    return this.api.get<any>(url).pipe(shareReplay(1));
  }

  public getDRCDropdownData(data: any): Observable<any> {
    const url = '/v1/backoffice/lookup/search';
    return this.api.post<any>(url, data);
  }

  public getDRCIbanNumberByAccount(accountNumber: string, bankId: string): Observable<any> {
    const url = `/v1/account/mappedaccountnumber?account=${accountNumber}&bankid=${bankId}`;
    return this.api.get<any>(url);
  }

  public updateRemitterAddress(ticketId: string, data: any): Observable<any> {
    const url = `/v1/transactions/${ticketId}/updateaddress`;
    return this.api.post<any>(url, data);
  }

  public getTransactionRequiredDocuments() {
    const url = `/v1/backoffice/documents?ActionFlow=CustomerFundTransfer103FlowV2`;
    return this.api.get<any>(url) as any as Observable<any>;
  }

  public uploadDocumentsV3(value: any) {
    const url = `/v3/documents/submit`;
    return this.api.post<any>(url, value);
  }

  public cleanUpDocuments(value: any, ticketId: string) {
    const url = `/v1/backoffice/documents/${ticketId}/cleandocuments`;
    return this.api.post<any>(url, value);
  }

  updateRequiredTransactionDocs(
    docData: {
      fileName?: string;
      filePresent?: boolean;
      document: {
        data: string;
        docCode: string;
        filename: string;
        format: string;
      };
      file?: any;
    }[],
    docsInfo: any[],
    docArray: any,
    docValidators: any[]
  ) {
    const documentArray = docArray as FormArray;
    const uploads: {
      file: string;
      format: string;
      name: string;
      docCode: string;
    }[] = docData.map(doc => {
      if (doc.file) {
        return {
          file: doc.document.data,
          format: doc.document.format,
          name: doc.document.filename,
          docCode: doc.document?.docCode || '',
        };
      } else {
        return {
          file: '',
          format: '',
          name: doc.document.filename,
          docCode: '',
        };
      }
    });

    for (let i = 0; i < uploads.length; i++) {
      const activeUpload = uploads[i];
      const documentName = docData[i].fileName || activeUpload.name;
      const activeValidator = docValidators.find(
        validator => validator.fileName.toLowerCase() === documentName.toLowerCase()
      );
      const docIndex = documentArray.controls.findIndex(
        (doc: any) => doc?.get('name')?.value === activeValidator?.formName
      );

      if (docIndex !== -1) {
        documentArray
          .at(docIndex)
          .get('value')
          ?.setValue(docData[i].filePresent ? documentName : activeUpload.file.split(',')[1] || '');
      }
    }

    return uploads;
  }

  getBeneficiaryBankCode(bankId: string) {
    const url = `/v1/backoffice/lookup/banksList/${bankId}`;
    return this.api.get<any>(url);
  }

  getBeneficiaryBankCode2(countryCode: string) {
    const url = `/v1/datalookup/bank-query?country=${countryCode}`;
    return this.api.get<any>(url);
  }
  getBeneficiaryBranchCode(bankId: string, bankCode: string) {
    const url = `/v1/backoffice/lookup/branchCodeList/${bankId}/${bankCode}`;
    return this.api.get<any>(url);
  }

  getLicenseCategoriesList(category: string = '') {
    // https://api-uat.equitygroupholdings.com/v1/backoffice/lookup/search
    const url = `/v1/backoffice/lookup/search`;
    const data = [
      {
        Name: 'licensecode',
        SearchTerm: '',
        ParentValue: category,
      },
    ];

    return this.api.post<any>(url, data, {
      headers: { skipLoadingInterceptor: 'true' },
    });
  }
}
